import { getDocumentStatus } from "@repo/types";
import type { Document } from "./types";

/**
 * Derived action-capability flags for a single document slot.
 */
export type DocumentActionStatus = {
  /** Whether the document URL is resolvable and can be opened/previewed. */
  canOpen: boolean;
  /** Whether the document can be downloaded. */
  canDownload: boolean;
  /** Whether the document can be replaced (upload a new version). */
  canChange: boolean;
  /** Whether the document can be deleted. */
  canDelete: boolean;
  /** Resolved URL safe to use for open / download / preview. */
  documentUrl: string;
};

/**
 * Computes action-capability flags for a document value.
 *
 * @param value       - The document object from the form / query.
 * @param resolvedUrl - Pre-resolved URL (from the caller's existing useMemo).
 * @param canEdit     - External edit-mode flag (false = read-only; suppresses
 *                      change and delete regardless of status).
 */
export function getDocumentActionStatus(
  value: Document | undefined,
  resolvedUrl: string,
  canEdit = true
): DocumentActionStatus {
  const result = getDocumentStatus(value);

  const hasMeaningfulUrl = Boolean(resolvedUrl);
  const isAccessible = result.hasAccessibleFile && hasMeaningfulUrl;

  return {
    canOpen: isAccessible,
    canDownload: isAccessible,
    canChange: canEdit,
    canDelete: canEdit && result.status === "APPROVED" && hasMeaningfulUrl,
    documentUrl: isAccessible ? resolvedUrl : "",
  };
}
